'use client';
import { Building05 } from '@v2/ui/Icon/General';
import { Plane, Ticket01 } from '@v2/ui/Icon/MapsAndTravel';
import { Backpack } from '@v2/ui/Icon/education';
import { UserCheck02 } from '@v2/ui/Icon/UserCheck02/UserCheck02';
import { UsersPlus } from '@v2/ui/Icon/UsersPlus/UsersPlus';
import { typographyVariants } from '@v2/ui/Typography';
import { cn } from '@v2/utils/cn';
import { useId, useState } from 'react';

interface SectionData {
  sectionType: string;
  text: string;
  originalValue: any;
  headerIndex: number;
  presentation?: 'team';
  overview?: boolean;
}

const parseSectionText = ({
  sectionType,
  text,
  headerIndex,
  originalValue,
  presentation,
  overview,
}: SectionData) => {
  const headerIcons = [
    <Backpack size="24" key="backpack" />,
    <UserCheck02 size="24" key="user-check-02" />,
    <UsersPlus size="24" key="users-plus" />,
    <Ticket01 size="24" key="ticket-01" />,
    <Building05 size="24" key="building-05" className="text-[#1D2939]" />,
    <Plane size="24" key="plane" />,
  ];
  const textTypes = {
    ['title']: (content: string) => (
      <h2
        className={
          presentation === 'team'
            ? 'mb-5 text-xl font-bold text-gray-800 md:mb-6 md:text-3xl'
            : 'mb-5 text-lg font-bold text-gray-800 md:mb-8 md:text-2xl'
        }
      >
        About {content}
      </h2>
    ),
    ['header']: (content: string) =>
      presentation === 'team' ? (
        <h3
          className={
            overview
              ? 'sr-only'
              : 'mb-2 mt-6 text-base font-semibold text-gray-800 md:text-lg'
          }
        >
          {content}
        </h3>
      ) : (
        <div className="mb-3 mt-8 flex flex-row items-center gap-4">
          {headerIcons[headerIndex] || <Backpack key="backpack" />}
          <h2 className="text-base font-bold text-gray-800 md:text-xl">
            {content}
          </h2>
        </div>
      ),
    ['p']: (content: string) => (
      <p
        className={`${presentation === 'team' ? 'leading-6' : 'ml-10'} text-sm font-medium text-gray-500 md:text-base`}
      >
        {content}
      </p>
    ),

    ['bullets']: (_content: string[]) => (
      <ul
        className={`my-2 list-disc flex-col gap-y-0.5 ${presentation === 'team' ? 'ml-5' : 'ml-14'}`}
      >
        {originalValue.map((text) => (
          <li
            className={cn(
              typographyVariants({ size: 'tmd' }),
              'font-normal text-gray-500'
            )}
            key={text}
          >
            {text}
          </li>
        ))}
      </ul>
    ),
  };
  return textTypes[sectionType](text);
};

interface TeamDescriptionProps {
  teamDescription: any[];
  isCollapsible?: boolean;
  presentation?: 'team';
  section?: 'overview' | 'details';
}

export const TeamDescription = ({
  teamDescription,
  isCollapsible = true,
  presentation,
  section,
}: TeamDescriptionProps) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState(!isCollapsible);
  const contentId = useId();
  const DescriptionContainer = presentation === 'team' ? 'section' : 'div';
  const firstParagraphIndex = teamDescription.findIndex(
    (item) => item.p || item.bullets
  );
  const detailsIndex =
    firstParagraphIndex < 0
      ? -1
      : teamDescription.findIndex(
          (item, index) => index > firstParagraphIndex && item.header
        );
  const splitIndex = detailsIndex < 0 ? teamDescription.length : detailsIndex;
  const sections =
    section === 'overview'
      ? teamDescription.slice(0, splitIndex)
      : section === 'details'
        ? teamDescription.slice(splitIndex)
        : teamDescription;

  if (!sections.length) return null;

  const toggleSection = () => {
    setIsSectionExpanded((oldValue) => !oldValue);
  };
  let headerIndex = -1;
  return (
    <DescriptionContainer className="pr-4 md:pr-0">
      <div
        id={contentId}
        className={`w-full ${isSectionExpanded ? '' : 'max-h-80 overflow-hidden'} duration-1000 ease-in`}
      >
        {sections.map((item, index) => {
          const [sectionType, text] = Object.entries(item)[0];
          if (sectionType === 'header') {
            headerIndex++;
          }
          return (
            <div key={`${sectionType}_${index}`}>
              {parseSectionText({
                sectionType,
                text: String(text),
                originalValue: text,
                headerIndex,
                presentation,
                overview: section === 'overview',
              })}
            </div>
          );
        })}
      </div>
      {isCollapsible ? (
        <>
          <div
            className={`${isSectionExpanded ? 'hidden' : 'relative'} z-10 mt-[-100px] h-28 w-full bg-gradient-to-b from-white/20 to-white`}
          ></div>

          <button
            onClick={toggleSection}
            aria-expanded={isSectionExpanded}
            aria-controls={contentId}
            className="mt-6 w-full rounded-lg border border-gray-200 bg-[#F7F8FF] py-4 font-semibold text-gray-800"
          >
            {isSectionExpanded ? 'Show less' : 'Show more'}
          </button>
        </>
      ) : null}
    </DescriptionContainer>
  );
};
